// React & Libraries
import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

// UI Components
import H3Text from '@Uilib/h3Text';

// Custom Components
import ViewStockTransfer from '@Components/Inventory/StockTransfer/ViewStockTransfer';
import StockTransferPR from '@Components/Purchase/PurchaseRequests/ViewPurchaseRequest/StockTransferPR';
import RfqForm from '@Components/Purchase/Rfq/RfqForm';
import PurchaseRequestLines from './PurchaseRequestLines';
import ViewLoadingSkull from '../../../Common/ViewLoadingSkull';
import PurchaseIndentForm from '../../PurchaseIndent/PurchaseIndentForm';
import ViewPurchaseIndent from '../../PurchaseIndent/ViewPurchaseIndent';
import ViewDocRightSection from '../../../Common/ViewDocRightSection';
import ViewDocumentFooterDetails from '../../../Common/ViewDocFooterLeftSection';
import ViewPrHeader from './ViewPrHeader';
import ViewPrDetails from './ViewPrDetails';
import PRZDrawer from '../../../Common/UI/PRZDrawer';

// Actions
import PurchaseRequestActions from '@Actions/purchaseRequestActions';
import AttachmentActions from '@Actions/attachmentActions';
import ActivityLogActions from '@Actions/activityLogActions';
import AnalyticsActions from '@Actions/application/analyticsActions';

// Helpers & Constants
import Helpers from '@Apis/helpers';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import {
  initialState,
  handleFileChange,
  getLinkedDocumentInfo,
} from './helper';

// Assets & Styles
import './style.scss';

const ViewPurchaseRequest = (props) => {
  const {
    user, org, selectedPrId, selectedTenantId, isQuickView, selectedPurchaseRequest, getPurchaseRequestById, getPurchaseRequestByIdLoading, updatePurchaseRequestStatusLoading, deletePurchaseRequestLoading, updatePurchaseRequestStatus, deletePurchaseRequest, updatePrWorkflowStepLoading, updatePrWorkflowStep, getAttachmentById, updateAttachment, selectedAttachment, getAttachmentByIdLoading, updateAttachmentLoading, match, getActivityLogSuccess, getPurchaseRequestByIdSuccess, downloadDocument, MONEY, priceMasking,
  } = props;

  const { tenantId, prId } = match.params;

  const [state, setState] = useState(initialState);

  const { isUserReady, isUserReadyForPrefillData, currentPrId, fileList, selectedTags, visibleColumns, cfPurchaseRequestLine, selectedPIId, selectedSTId, showNewStForm, showStockTransferModal, currentUpdate, showCreateRfqDrawer, showPIForm, showPIDrawer,
  } = state;

  const updateState = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  useEffect(() => {
    getActivityLogSuccess(null);
    return () => getPurchaseRequestByIdSuccess(null);
  }, []);

  useEffect(() => {
    if (user?.tenant_info?.tenant_id && !isUserReady) {
      const finalPrId = isQuickView ? selectedPrId : prId;
      const finalTenantId = isQuickView ? selectedTenantId : (tenantId || user?.tenant_info?.tenant_id);
      const tenantPermission = Helpers.getTenantEntityPermission(
        user?.user_tenants,
        Helpers.permissionEntities.PURCHASE_REQUEST,
        Helpers.permissionTypes.READ
      )?.join(',');

      getPurchaseRequestById(finalPrId, tenantPermission, () => { });
      updateState('isUserReady', true);
      updateState('currentPrId', finalPrId);
    }
  }, [user, isUserReady]);

  useEffect(() => {
    if (selectedPurchaseRequest && !isUserReadyForPrefillData && isUserReady) {
      const lineCf = selectedPurchaseRequest?.purchase_requistion_lines?.[0]?.custom_fields;
      setState((prev) => ({
        ...prev,
        fileList: selectedPurchaseRequest?.attachments || [],
        isUserReadyForPrefillData: true,
        cfPurchaseRequestLine: lineCf,
        selectedTags: selectedPurchaseRequest?.tags || [],
        visibleColumns: CustomFieldHelpers.updateVisibleColumns(lineCf, prev.visibleColumns),
      }));
    }
  }, [selectedPurchaseRequest]);

  useEffect(() => {
    if (selectedAttachment && state.isGetAttachment && !getAttachmentByIdLoading && !updateAttachmentLoading) {
      setState((prev) => ({
        ...prev,
        fileList: selectedAttachment,
        isGetAttachment: false,
      }));
    }
  }, [selectedAttachment, state.isGetAttachment, getAttachmentByIdLoading, updateAttachmentLoading]);

  const onFileUploadChange = (fileListData) => {
    handleFileChange(
      fileListData,
      selectedPurchaseRequest,
      updateAttachment,
      getAttachmentById,
      setState
    );
  };

  const grandTotal = selectedPurchaseRequest?.purchase_requistion_lines?.reduce((acc, curr) => acc + (curr.target_price * curr.quantity), 0);
  const fixedPriceEnabled = user?.tenant_info?.purchase_config?.sub_modules?.purchase_request?.settings?.purchase_request_target_price_required;

  const { isDataMaskingPolicyEnable, isHideCostPrice } = priceMasking;
  const fixedMenuBar = user?.side_menu_bar_type === 'FIXED';

  const linkedDocumentsInfo = getLinkedDocumentInfo(
    selectedPurchaseRequest,
    user,
    isQuickView,
    state.showPIDrawer,
    state.showStockTransferModal,
    setState,
    isDataMaskingPolicyEnable,
    isHideCostPrice
  );

  return (
    <div className={`view-document__wrapper ${!isQuickView ? 'view-document__wrapper-page' : ''}`}>
      {!getPurchaseRequestByIdLoading ? (
        <div className="ant-row">
          <div className={`${fixedMenuBar ? 'ant-col-md-16' : 'ant-col-md-17'} ant-col-xs-24`}>
            <div className={`view-left__wrapper ${!isQuickView ? 'is-page-view' : ''}`}>
              <ViewPrHeader
                selectedPurchaseRequest={selectedPurchaseRequest}
                user={user}
                isQuickView={isQuickView}
                match={match}
                updatePurchaseRequestStatus={updatePurchaseRequestStatus}
                downloadDocument={downloadDocument}
                deletePurchaseRequest={deletePurchaseRequest}
                getPurchaseRequestById={getPurchaseRequestById}
                updatePurchaseRequestStatusLoading={updatePurchaseRequestStatusLoading}
                deletePurchaseRequestLoading={deletePurchaseRequestLoading}
                currentUpdate={currentUpdate}
                setCurrentUpdate={(value) => updateState('currentUpdate', value)}
                setSendForApprovalConfirmation={(val) => updateState('sendForApprovalConfirmation', val)}
                setShowNewStForm={(val) => updateState('showNewStForm', val)}
                setShowCreateRfqDrawer={(val) => updateState('showCreateRfqDrawer', val)}
                setShowPIForm={(val) => updateState('showPIForm', val)}
              />
              <ViewPrDetails selectedPurchaseRequest={selectedPurchaseRequest} />
              <br />
              <PurchaseRequestLines
                poLines={selectedPurchaseRequest?.purchase_requistion_lines?.map(item => ({ ...item, lineCustomFields: item?.custom_fields })) || []}
                selectedPurchaseOrder={selectedPurchaseRequest}
                cfPurchaseRequestLine={cfPurchaseRequestLine}
                fixedPriceEnabled={fixedPriceEnabled}
                selectedPurchaseRequest={selectedPurchaseRequest}
                visibleColumns={visibleColumns}
                setVisibleColumns={(data) => updateState('visibleColumns', data)}
              />
              <div className="view-document__totals-wrapper">
                <ViewDocumentFooterDetails
                  remark={selectedPurchaseRequest?.remark}
                  fileList={fileList}
                  disableAttachment={getAttachmentByIdLoading || updateAttachmentLoading}
                  onFileChange={onFileUploadChange}
                  canUpdateAttachment={Helpers.getPermission(
                    Helpers.permissionEntities.PURCHASE_REQUEST,
                    Helpers.permissionTypes.UPDATE,
                    user
                  )}
                />
                {!(isDataMaskingPolicyEnable && isHideCostPrice) && fixedPriceEnabled && (
                  <div className="view-document__totals">
                    <div className="view-document__totals-field view-document__totals-field-total" style={{ border: '0px', margin: '0px', padding: '0px' }}>
                      <H3Text text="Grand Total" className="view-document__totals-field-name" />
                      <H3Text text={MONEY(grandTotal)} className="view-document__totals-field-value" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <ViewDocRightSection
            entityType="PURCHASE_REQUISITION"
            entityId={selectedPurchaseRequest?.purchase_requisition_id}
            MONEY={MONEY}
            user={user}
            isQuickView={isQuickView}
            tagSelectorProps={{
              isEnabled: true,
              selectedTags,
              isMultipleTagsAllowed: true,
              tagSearchAllowed: true,
              onChange: (value) => updateState('selectedTags', value),
              placeholder: "Select Tags",
            }}
            workflowTimelineProps={{
              isEnabled: selectedPurchaseRequest?.status !== 'DRAFT',
              workflowSteps: selectedPurchaseRequest?.workflow_steps || {},
              workflowStepId: selectedPurchaseRequest?.workflow_step_id,
              loading: updatePrWorkflowStepLoading,
              updateStep: updatePrWorkflowStep,
              callback: () => {
                getPurchaseRequestById(currentPrId, Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_REQUEST, Helpers.permissionTypes.READ)?.join(','), () => { });
              },
            }}
            activityLogProps={{
              isEnabled: selectedPurchaseRequest,
              entityName: 'Purchase Requisition',
              entityType: "purchase_requisition"
            }}
            linkedDocumentsInfo={linkedDocumentsInfo}
          />
        </div>
      ) : (
        <ViewLoadingSkull isQuickView={isQuickView} fixedMenuBar={fixedMenuBar} />
      )}

      <PRZDrawer
        open={showNewStForm}
        onClose={() => updateState('showNewStForm', false)}
        title="Create Stock Transfer"
        width={1080}
        headerWidth={1030}
      >
        <StockTransferPR
          selectedPurchaseRequest={selectedPurchaseRequest}
          department={selectedPurchaseRequest?.tenant_department_alias_name}
          departmentId={selectedPurchaseRequest?.department_id}
          callback={() => {
            updateState('showNewStForm', false);
            getPurchaseRequestById(
              isQuickView ? selectedPrId : prId,
              Helpers.getTenantEntityPermission(
                user?.user_tenants,
                Helpers.permissionEntities.PURCHASE_REQUEST,
                Helpers.permissionTypes.READ
              )?.join(','),
              () => { }
            );
          }}
          isOtherDepartmentStAllowed={
            !org?.organisation?.[0]?.purchase_config?.sub_modules?.purchase_request?.settings?.allow_st_from_other_departments
          }
        />
      </PRZDrawer>

      <PRZDrawer
        open={showStockTransferModal}
        onClose={() => {
          getPurchaseRequestById(isQuickView ? selectedPrId : prId, Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_REQUEST, Helpers.permissionTypes.READ)?.join(','), () => { });
          updateState('showStockTransferModal', false);
        }}
        title="View Stock Transfer"
        width={1030}
        headerWidth={980}
      >
        <ViewStockTransfer
          selectedSTId={selectedSTId}
          callback={() => {
            updateState('showStockTransferModal', false);
            getPurchaseRequestById(isQuickView ? selectedPrId : prId, Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_REQUEST, Helpers.permissionTypes.READ)?.join(','), () => { });
          }}
          isQuickView
        />
      </PRZDrawer>

      <PRZDrawer
        open={showPIDrawer}
        onClose={() => {
          getPurchaseRequestById(isQuickView ? selectedPrId : prId, Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_REQUEST, Helpers.permissionTypes.READ)?.join(','), () => { });
          updateState('showPIDrawer', false);
          updateState('selectedPIId', null);
        }}
        title="View Purchase Indent"
        width={1030}
        headerWidth={980}
      >
        <ViewPurchaseIndent
          selectedPIId={selectedPIId}
          callback={() => {
            updateState('showPIDrawer', false);
            getPurchaseRequestById(isQuickView ? selectedPrId : prId, Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_REQUEST, Helpers.permissionTypes.READ)?.join(','), () => { });
          }}
          isQuickView
        />
      </PRZDrawer>

      <PRZDrawer
        open={showCreateRfqDrawer}
        onClose={() => updateState('showCreateRfqDrawer', false)}
        title="Create Request for Quotation"
        width={1150}
        headerWidth={1100}
      >
        <RfqForm
          selectedPurchaseRequest={selectedPurchaseRequest}
          callback={() => {
            updateState('showCreateRfqDrawer', false);
            getPurchaseRequestById(currentPrId, Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_REQUEST, Helpers.permissionTypes.READ)?.join(','), () => { });
          }}
        />
      </PRZDrawer>

      <PRZDrawer
        open={showPIForm}
        onClose={() => updateState('showPIForm', false)}
        title="Create Purchase Indent"
        width={1150}
        headerWidth={1100}
      >
        <PurchaseIndentForm
          selectedPR={selectedPurchaseRequest}
          callback={() => {
            updateState('showPIForm', false);
            getPurchaseRequestById(isQuickView ? selectedPrId : prId, Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_REQUEST, Helpers.permissionTypes.READ)?.join(','), () => { });
          }}
        />
      </PRZDrawer>
    </div>
  );
};

const mapStateToProps = ({ UserReducers, PurchaseRequestReducers, AttachmentReducers }) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  org: UserReducers.org,
  getPurchaseRequestByIdLoading: PurchaseRequestReducers.getPurchaseRequestByIdLoading,
  selectedPurchaseRequest: PurchaseRequestReducers.selectedPurchaseRequest,
  updatePurchaseRequestStatusLoading: PurchaseRequestReducers.updatePurchaseRequestStatusLoading,
  deletePurchaseRequestLoading: PurchaseRequestReducers.deletePurchaseRequestLoading,
  updatePrWorkflowStepLoading: PurchaseRequestReducers.updatePrWorkflowStepLoading,
  selectedAttachment: AttachmentReducers.selectedAttachment,
  getAttachmentByIdLoading: AttachmentReducers.getAttachmentByIdLoading,
  updateAttachmentLoading: AttachmentReducers.updateAttachmentLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  getPurchaseRequestById: (prId, tenantId, callback) =>
    dispatch(PurchaseRequestActions.getPurchaseRequestById(prId, tenantId, callback)),
  updatePurchaseRequestStatus: (payload, callback) =>
    dispatch(PurchaseRequestActions.updatePurchaseRequestStatus(payload, callback)),
  deletePurchaseRequest: (payload, callback) =>
    dispatch(PurchaseRequestActions.deletePurchaseRequest(payload, callback)),
  updatePrWorkflowStep: (payload, callback) =>
    dispatch(PurchaseRequestActions.updatePrWorkflowStep(payload, callback)),
  getPurchaseRequestByIdSuccess: (selectedPr) =>
    dispatch(PurchaseRequestActions.getPurchaseRequestByIdSuccess(selectedPr)),
  getAttachmentById: (entityId, entityName, callback) =>
    dispatch(AttachmentActions.getAttachmentById(entityId, entityName, callback)),
  updateAttachment: (payload, callback) =>
    dispatch(AttachmentActions.updateAttachment(payload, callback)),
  getActivityLogSuccess: (activityLog) =>
    dispatch(ActivityLogActions.getActivityLogSuccess(activityLog)),
  downloadDocument: (payload, document) =>
    dispatch(AnalyticsActions.downloadDocument(payload, document)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ViewPurchaseRequest));